import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid/index';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { useForm, Controller } from 'react-hook-form';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import Loader from '../Loader/Loader';
import { PDFDownloadLink } from '@react-pdf/renderer';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import CreateJobPDF from '../CreateJobPDF/CreateJobPDF';
import PdfPrimaryDownloadButton from '../PdfPrimaryDownloadButton/PdfPrimaryDownloadButton';
import api from '../../services';
import toaster from '../../utils/toaster';
import { emitInteractiveTourProgress } from '../../tutorial/tourBridge';

const useStyles = makeStyles((theme) => ({
  introBox: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  formPaper: {
    padding: theme.spacing(3, 2.5),
    borderRadius: 'var(--radius-lg)',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'var(--shadow-xs)',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
    },
  },
  formBox: {
    display: 'flex',
    flexDirection: 'column',
  },
  stackForm: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  section: {
    marginTop: theme.spacing(3),
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  sectionTitle: {
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  formControl: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    width: '100%',
    maxWidth: '100%',
  },
  gridItem: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    width: '100%',
    '& > *': {
      flex: '1 1 240px',
      minWidth: 0,
    },
  },
  formHelper: {
    textAlign: 'right',
    cursor: 'pointer',
    marginTop: '-10px',
    marginRight: '4px',
  },
  actionCont: {
    display: 'flex',
    width: '100%',
    maxWidth: 900,
    marginLeft: 'auto',
    marginRight: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  actionBtn: {
    borderRadius: 8,
    height: 44,
    padding: '0 32px',
    textTransform: 'none',
    fontWeight: 600,
    letterSpacing: '0.02em',
    boxShadow: 'none',
  },
}));

// Create: 3 steps (Basic details → Locations → Pricing & date). Edit: flat single form.
const FORM_STEPS = ['Basic details', 'Locations', 'Pricing & date'];

const CreateJobForm = ({
  onCreateJob,
  onUpdateJob,
  isEditing,
  job: jobProp,
  quotationPrefill,
}) => {
  const job = jobProp || {};
  const {
    gcnno,
    carGcnno,
    consignorF,
    consignorL,
    consigneeF,
    consigneeL,
    contact,
    email,
    oaddress1,
    oaddress2,
    ocity,
    ostate,
    opincode,
    daddress1,
    daddress2,
    dcity,
    dstate,
    dpincode,
    status,
    car,
    insuranceP,
    insuranceA,
    insuranceCarP,
    insuranceCarA,
    type,
    date,
    items,
    gst,
    item,
    personContactId,
    insurerContactId,
    companyContactId,
    transporterContactId,
  } = job;

  const classes = useStyles();
  const getDefaultValue = (editing, value) => (editing ? value : '');
  const [isDownloadingPDF, updateIsDownloadingPDF] = useState(false);
  const [jobDetails, updateJobDetails] = useState();
  const [activeStep, setActiveStep] = useState(0);
  const [directoryContacts, setDirectoryContacts] = useState([]);

  const { handleSubmit, control, errors, getValues, watch, setValue, trigger } = useForm({
    mode: 'onSubmit',          // validate only on submit — avoids RHF v6 untouched-field issues
    defaultValues: {
      gcnno: getDefaultValue(isEditing, gcnno),
      carGcnno: getDefaultValue(isEditing, carGcnno),
      consignorF: getDefaultValue(isEditing, consignorF),
      consignorL: getDefaultValue(isEditing, consignorL),
      consigneeF: getDefaultValue(isEditing, consigneeF),
      consigneeL: getDefaultValue(isEditing, consigneeL),
      contact: getDefaultValue(isEditing, contact),
      email: getDefaultValue(isEditing, email),
      oaddress1: getDefaultValue(isEditing, oaddress1),
      oaddress2: getDefaultValue(isEditing, oaddress2),
      ocity: getDefaultValue(isEditing, ocity),
      ostate: getDefaultValue(isEditing, ostate),
      opincode: getDefaultValue(isEditing, opincode),
      daddress1: getDefaultValue(isEditing, daddress1),
      daddress2: getDefaultValue(isEditing, daddress2),
      dcity: getDefaultValue(isEditing, dcity),
      dstate: getDefaultValue(isEditing, dstate),
      dpincode: getDefaultValue(isEditing, dpincode),
      status: getDefaultValue(isEditing, status),
      insuranceP: getDefaultValue(isEditing, insuranceP),
      insuranceA: getDefaultValue(isEditing, insuranceA),
      insuranceCarP: getDefaultValue(isEditing, insuranceCarP),
      insuranceCarA: getDefaultValue(isEditing, insuranceCarA),
      items: getDefaultValue(isEditing, items),
      gst: getDefaultValue(isEditing, gst),
      item: getDefaultValue(isEditing, item),
      personContactId: getDefaultValue(isEditing, personContactId),
      insurerContactId: getDefaultValue(isEditing, insurerContactId),
      companyContactId: getDefaultValue(isEditing, companyContactId),
      transporterContactId: getDefaultValue(isEditing, transporterContactId),
      type: isEditing ? type : 'household',
      date: getDefaultValue(isEditing, date),
      car: isEditing ? car : true,
    },
  });

  useEffect(() => {
    if (!quotationPrefill || isEditing) return;
    Object.entries(quotationPrefill).forEach(([key, val]) => {
      setValue(key, val, { shouldValidate: false, shouldDirty: false });
    });
  }, [quotationPrefill, isEditing, setValue]);

  const gcnnoVal = watch('gcnno');
  const consignorFVal = watch('consignorF');
  const prevGcnTourRef = useRef('');
  const prevConsignorTourRef = useRef('');
  const prevActiveStepTourRef = useRef(0);

  useEffect(() => {
    const t = String(gcnnoVal || '').trim();
    if (t.length >= 2 && String(prevGcnTourRef.current || '').trim().length < 2) {
      emitInteractiveTourProgress({ type: 'gcn' });
    }
    prevGcnTourRef.current = t;
  }, [gcnnoVal]);

  useEffect(() => {
    const raw = String(consignorFVal || '');
    const name = raw.replace(/^Mr\.?\s*/i, '').trim();
    const prevRaw = String(prevConsignorTourRef.current || '');
    const prevName = prevRaw.replace(/^Mr\.?\s*/i, '').trim();
    if (name.length >= 2 && prevName.length < 2) {
      emitInteractiveTourProgress({ type: 'consignor' });
    }
    prevConsignorTourRef.current = raw;
  }, [consignorFVal]);

  useEffect(() => {
    if (prevActiveStepTourRef.current === 0 && activeStep >= 1) {
      emitInteractiveTourProgress({ type: 'form-step-1' });
    }
    prevActiveStepTourRef.current = activeStep;
  }, [activeStep]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/tenant/contacts');
        if (cancelled) return;
        setDirectoryContacts(Array.isArray(data?.contacts) ? data.contacts : []);
      } catch (_) {
        // optional enrichment for dropdowns
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const STEP_REQUIRED_FIELDS = {
    0: ['consignorF', 'consigneeF', 'contact'],
    1: [],
    2: ['date'],
  };

  const handleContinue = async () => {
    const fieldsToCheck = STEP_REQUIRED_FIELDS[activeStep] || [];
    if (fieldsToCheck.length) {
      const valid = await trigger(fieldsToCheck);
      if (!valid) {
        toaster('Please fill in the required fields to continue.', { type: 'error' });
        return;
      }
    }
    setActiveStep((s) => Math.min(FORM_STEPS.length - 1, s + 1));
  };

  const onSubmitError = (errs) => {
    const errorFields = Object.keys(errs);
    if (!errorFields.length) return;
    const step0Fields = ['consignorF', 'consigneeF', 'contact', 'email'];
    const step1Fields = ['oaddress1', 'ocity', 'ostate', 'daddress1', 'dcity', 'dstate'];
    if (!isEditing) {
      if (errorFields.some((f) => step0Fields.includes(f))) setActiveStep(0);
      else if (errorFields.some((f) => step1Fields.includes(f))) setActiveStep(1);
      else setActiveStep(2);
    }
    const first = errs[errorFields[0]];
    const msg = first?.message || 'Please correct the highlighted fields before submitting.';
    toaster(msg, { type: 'error' });
  };

  const submit = (data) => {
    if (!isEditing && activeStep < FORM_STEPS.length - 1) return;
    updateJobDetails(data);
    if (isEditing) {
      onUpdateJob(data);
    } else {
      onCreateJob(data);
    }
  };

  watch(['car', 'type']);
  const values = getValues();

  return (
    <Grid container>
      {isDownloadingPDF ? (
        <PDFDownloadLink
          document={<CreateJobPDF invoice={{ firstName: 'sdf', LastName: 'sdfasdf' }} />}
          fileName='job.pdf'
        >
          {({ blob, url, loading, error }) =>
            loading ? <Loader /> : <PdfPrimaryDownloadButton>Download Now</PdfPrimaryDownloadButton>
          }
        </PDFDownloadLink>
      ) : (
        <Grid item lg={12} container className={classes.gridItem}>
          <Box className={classes.introBox}>
            <Paper className={classes.formPaper} elevation={0}>
              <form
                className={classes.stackForm}
                autoComplete='off'
                onSubmit={handleSubmit(submit, onSubmitError)}
                noValidate
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isEditing && activeStep < FORM_STEPS.length - 1) {
                    e.preventDefault();
                  }
                }}
              >
                {/* Step indicator — hidden in edit mode (single flat form) */}
                {!isEditing && (
                  <Box className={classes.section}>
                    <Box display='flex' style={{ gap: 8, marginBottom: 12 }}>
                      {FORM_STEPS.map((step, idx) => (
                        <Box
                          key={step}
                          style={{
                            flex: 1,
                            textAlign: 'center',
                            fontSize: 11,
                            fontWeight: 600,
                            borderRadius: 999,
                            padding: '7px 8px',
                            background: idx <= activeStep ? 'var(--brand)' : 'var(--gray-100)',
                            color: idx <= activeStep ? '#fff' : 'var(--text-secondary)',
                            cursor: idx < activeStep ? 'pointer' : 'default',
                          }}
                          onClick={() => { if (idx < activeStep) setActiveStep(idx); }}
                        >
                          {step}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* ── STEP 0: Details & Locations ── */}

                <Box className={classes.section} style={{ display: isEditing || activeStep === 0 ? 'block' : 'none' }}>
                  <Typography className={classes.sectionTitle}>Job document numbers</Typography>
                  <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                    <Box marginRight='10px' data-tour='job-gcn-field'>
                      <Controller
                        as={TextField}
                        control={control}
                        label='GCNNO'
                        variant='outlined'
                        className={classes.formControl}
                        name='gcnno'
                        error={!!errors.gcnno}
                        helperText={errors.gcnno && 'GCNNO is required'}
                      />
                    </Box>
                    <Controller
                      as={TextField}
                      control={control}
                      label='CAR GCNNO'
                      variant='outlined'
                      className={classes.formControl}
                      name='carGcnno'
                    />
                  </Grid>
                  <Grid item lg={12} container className={classes.gridItem}>
                    <Box marginRight='10px'>
                      <Controller
                        as={TextField}
                        control={control}
                        label='Items'
                        variant='outlined'
                        className={classes.formControl}
                        name='items'
                      />
                    </Box>
                    <Controller
                      as={TextField}
                      control={control}
                      label='Company GST.No'
                      variant='outlined'
                      className={classes.formControl}
                      name='gst'
                    />
                  </Grid>
                </Box>

                <Box className={classes.section} style={{ display: isEditing || activeStep === 0 ? 'block' : 'none' }}>
                  <Typography className={classes.sectionTitle}>Consignor</Typography>
                  <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                    <Box marginRight='10px' data-tour='job-consignor-first'>
                      <Controller
                        as={TextField}
                        control={control}
                        label='FirstName'
                        variant='outlined'
                        rules={{
                          required: 'Consignor first name is required',
                          validate: (v) => {
                            const name = String(v || '').replace(/^Mr\.?\s*/i, '').trim();
                            return name.length >= 2 || 'Enter at least 2 characters';
                          },
                        }}
                        className={classes.formControl}
                        name='consignorF'
                        defaultValue='Mr '
                        error={!!errors.consignorF}
                        helperText={errors.consignorF?.message}
                        required
                      />
                    </Box>
                    <Controller
                      as={TextField}
                      control={control}
                      label='LastName'
                      variant='outlined'
                      className={classes.formControl}
                      name='consignorL'
                    />
                  </Grid>
                </Box>

                <Box className={classes.section} style={{ display: isEditing || activeStep === 0 ? 'block' : 'none' }}>
                  <Typography className={classes.sectionTitle}>Consignee & contact</Typography>
                  <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                    <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                      <Box marginRight='10px'>
                        <Controller
                          as={TextField}
                          control={control}
                          label='FirstName'
                          variant='outlined'
                          rules={{
                            required: 'Consignee first name is required',
                            minLength: { value: 2, message: 'Enter at least 2 characters' },
                          }}
                          className={classes.formControl}
                          name='consigneeF'
                          error={!!errors.consigneeF}
                          helperText={errors.consigneeF?.message}
                          required
                        />
                      </Box>
                      <Controller
                        as={TextField}
                        control={control}
                        label='LastName'
                        variant='outlined'
                        className={classes.formControl}
                        name='consigneeL'
                      />
                    </Grid>
                    <Grid item lg={12} container className={classes.gridItem}>
                      <Box marginRight='10px'>
                        <Controller
                          as={TextField}
                          control={control}
                          label='Mobile'
                          variant='outlined'
                          rules={{
                            required: 'Mobile number is required',
                            minLength: { value: 7, message: 'Must be at least 7 digits' },
                            maxLength: { value: 15, message: 'Must be 15 digits or fewer' },
                            pattern: { value: /^[+\d][\d\s\-().]*$/, message: 'Enter a valid mobile number' },
                          }}
                          className={classes.formControl}
                          name='contact'
                          error={!!errors.contact}
                          helperText={errors.contact?.message}
                          required
                        />
                      </Box>
                      <Controller
                        as={TextField}
                        control={control}
                        label='Email'
                        variant='outlined'
                        rules={{ pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' } }}
                        className={classes.formControl}
                        name='email'
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box className={classes.section} style={{ display: isEditing || activeStep === 0 ? 'block' : 'none' }}>
                  <Typography className={classes.sectionTitle}>Reference contacts (optional)</Typography>
                  <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                    <Controller
                      as={
                        <TextField select label='Person' variant='outlined' className={classes.formControl}>
                          <MenuItem value=''>None</MenuItem>
                          {directoryContacts.filter((c) => c.type === 'partner').map((c) => (
                            <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                          ))}
                        </TextField>
                      }
                      control={control}
                      name='personContactId'
                      defaultValue=''
                    />
                    <Controller
                      as={
                        <TextField select label='Insurer' variant='outlined' className={classes.formControl}>
                          <MenuItem value=''>None</MenuItem>
                          {directoryContacts.filter((c) => c.type === 'insurer').map((c) => (
                            <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                          ))}
                        </TextField>
                      }
                      control={control}
                      name='insurerContactId'
                      defaultValue=''
                    />
                  </Grid>
                  <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                    <Controller
                      as={
                        <TextField select label='Company' variant='outlined' className={classes.formControl}>
                          <MenuItem value=''>None</MenuItem>
                          {directoryContacts.filter((c) => c.type === 'company_hr').map((c) => (
                            <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                          ))}
                        </TextField>
                      }
                      control={control}
                      name='companyContactId'
                      defaultValue=''
                    />
                    <Controller
                      as={
                        <TextField select label='Transporter' variant='outlined' className={classes.formControl}>
                          <MenuItem value=''>None</MenuItem>
                          {directoryContacts.filter((c) => c.type === 'transporter').map((c) => (
                            <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                          ))}
                        </TextField>
                      }
                      control={control}
                      name='transporterContactId'
                      defaultValue=''
                    />
                  </Grid>
                </Box>

                <Box className={classes.section} style={{ display: isEditing || activeStep === 1 ? 'block' : 'none' }}>
                  <Typography className={classes.sectionTitle}>Origin</Typography>
                  <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                    <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                      <Box marginRight='10px'>
                        <Controller
                          as={TextField}
                          control={control}
                          label='Address Line 1'
                          variant='outlined'
                          className={classes.formControl}
                          name='oaddress1'
                        />
                      </Box>
                      <Controller
                        as={TextField}
                        control={control}
                        label='Address Line 2'
                        variant='outlined'
                        className={classes.formControl}
                        name='oaddress2'
                      />
                    </Grid>
                    <Grid item lg={12} container className={classes.gridItem}>
                      <Box marginRight='10px'>
                        <Controller
                          as={TextField}
                          control={control}
                          label='City'
                          variant='outlined'
                          className={classes.formControl}
                          name='ocity'
                        />
                      </Box>
                      <Controller
                        as={TextField}
                        control={control}
                        label='State'
                        variant='outlined'
                        className={classes.formControl}
                        name='ostate'
                      />
                    </Grid>
                    <Grid item lg={12} container className={classes.gridItem}>
                      <Box>
                        <Controller
                          as={TextField}
                          control={control}
                          label='Pincode'
                          variant='outlined'
                          className={classes.formControl}
                          name='opincode'
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Box className={classes.section} style={{ display: isEditing || activeStep === 1 ? 'block' : 'none' }}>
                  <Typography className={classes.sectionTitle}>Destination</Typography>
                  <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                    <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                      <Box marginRight='10px'>
                        <Controller
                          as={TextField}
                          control={control}
                          label='Address Line 1'
                          variant='outlined'
                          className={classes.formControl}
                          name='daddress1'
                        />
                      </Box>
                      <Controller
                        as={TextField}
                        control={control}
                        label='Address Line 2'
                        variant='outlined'
                        className={classes.formControl}
                        name='daddress2'
                      />
                    </Grid>
                    <Grid item lg={12} container className={classes.gridItem}>
                      <Box marginRight='10px'>
                        <Controller
                          as={TextField}
                          control={control}
                          label='City'
                          variant='outlined'
                          className={classes.formControl}
                          name='dcity'
                        />
                      </Box>
                      <Controller
                        as={TextField}
                        control={control}
                        label='State'
                        variant='outlined'
                        className={classes.formControl}
                        name='dstate'
                      />
                    </Grid>
                    <Grid item lg={12} container className={classes.gridItem}>
                      <Controller
                        as={TextField}
                        control={control}
                        label='Pincode'
                        variant='outlined'
                        className={classes.formControl}
                        name='dpincode'
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* ── STEP 1: Pricing & date ── */}

                <Box className={classes.section} style={{ display: isEditing || activeStep === 2 ? 'block' : 'none' }}>
                  <Typography className={classes.sectionTitle}>Pricing & other details</Typography>
                  <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                    <Grid item lg={12} container spacing={2} className={classes.gridItem}>
                      <Box marginRight='10px'>
                        <FormControl variant='outlined' className={classes.formControl}>
                          <InputLabel id='Type'>Type</InputLabel>
                          <Controller
                            as={
                              <Select labelId='type' label='Type'>
                                <MenuItem value='household'>Household</MenuItem>
                                <MenuItem value='car'>Car</MenuItem>
                                <MenuItem value='both'>Both</MenuItem>
                              </Select>
                            }
                            name='type'
                            control={control}
                            defaultValue='household'
                          />
                        </FormControl>
                      </Box>
                      <Controller
                        as={TextField}
                        control={control}
                        label='Household Insurance Percentage'
                        variant='outlined'
                        className={classes.formControl}
                        name='insuranceP'
                      />
                    </Grid>

                    <Grid item lg={12} container className={classes.gridItem}>
                      <Box marginRight='10px'>
                        <Controller
                          as={TextField}
                          control={control}
                          label='Household Insurance Amount'
                          variant='outlined'
                          className={classes.formControl}
                          name='insuranceA'
                        />
                      </Box>
                      <Controller
                        as={TextField}
                        control={control}
                        label='Car Insurance Percentage'
                        variant='outlined'
                        className={classes.formControl}
                        name='insuranceCarP'
                      />
                    </Grid>

                    <Grid item lg={12} container className={classes.gridItem}>
                      <Box marginRight='10px'>
                        <Controller
                          as={TextField}
                          control={control}
                          label='Car Insurance Amount'
                          variant='outlined'
                          className={classes.formControl}
                          name='insuranceCarA'
                        />
                      </Box>
                      <Box>
                        <Typography style={{ marginBottom: 4 }}>Move date</Typography>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                          <Controller
                            as={
                              <KeyboardDatePicker
                                disableToolbar
                                variant='inline'
                                format='dd/MM/yyyy'
                                margin='normal'
                                id='date-picker-inline'
                                KeyboardButtonProps={{
                                  'aria-label': 'change date',
                                  type: 'button',
                                }}
                              />
                            }
                            control={control}
                            name='date'
                            rules={{ required: 'Move date is required' }}
                            error={!!errors.date}
                            helperText={errors.date?.message || 'dd/mm/yyyy'}
                          />
                        </MuiPickersUtilsProvider>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Actions */}
                <div className={classes.actionCont}>
                  <Box display='flex' width='100%' style={{ gap: 10 }}>
                    {isEditing ? (
                      <Button
                        className={classes.actionBtn}
                        fullWidth
                        variant='contained'
                        color='primary'
                        type='submit'
                      >
                        Update job
                      </Button>
                    ) : (
                      <>
                        <Button
                          className={classes.actionBtn}
                          fullWidth
                          type='button'
                          variant='outlined'
                          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                          disabled={activeStep === 0}
                        >
                          Back
                        </Button>
                        {activeStep < FORM_STEPS.length - 1 ? (
                          <Button
                            className={classes.actionBtn}
                            fullWidth
                            type='button'
                            variant='contained'
                            color='primary'
                            data-tour='job-step-continue'
                            onClick={handleContinue}
                          >
                            Continue
                          </Button>
                        ) : (
                          <Button
                            className={classes.actionBtn}
                            fullWidth
                            variant='contained'
                            color='primary'
                            type='submit'
                          >
                            Create job
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </div>
              </form>
            </Paper>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default CreateJobForm;
